import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/primitives";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/lib/auth";
import { getStats } from "@/lib/api";
import { Sprout, LayoutGrid, Star, Coins, ChevronRight, Plus, Users, ShieldCheck } from "lucide-react";

export default function Home() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { getStats().then(setStats).catch(() => {}); }, []);

  const firstName = (user?.name || "").split(" ")[0] || "Gardener";

  return (
    <div data-testid="home-page">
      <PageHeader
        kicker={`Kia ora, ${firstName}`}
        title={user?.garden_name || "Your Patch"}
        subtitle={user?.location || "A pocket almanack for your dahlia patch"}
      />

      <section className="h-card p-5 sm:p-6 flex items-center gap-4" data-testid="home-profile">
        {user?.avatar_data_url
          ? <img src={user.avatar_data_url} alt={user.name} className="w-16 h-16 rounded-full object-cover bg-cream-deep" />
          : <BrandLogo size={64} />}
        <div className="flex-1 min-w-0">
          <div className="kicker">The Keeper</div>
          <div className="font-display text-2xl text-sage-dark truncate">{user?.name}</div>
          <div className="text-ink-soft text-base truncate">{user?.email}</div>
          {user?.location && <div className="font-display italic text-ink-soft text-base">{user.location}</div>}
        </div>
        <Link to="/profile" className="chip" data-testid="home-edit-profile">Edit</Link>
      </section>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5" data-testid="home-tiles">
        <Tile to="/garden" icon={Sprout} label="Plants" value={stats?.total_plants ?? "—"} testid="home-tile-plants" />
        <Tile to="/garden" icon={Star} label="Favourites" value={stats?.favorites ?? "—"} testid="home-tile-favs" accent />
        <Tile to="/beds" icon={LayoutGrid} label="Beds" value={stats?.bed_count ?? "—"} testid="home-tile-beds" />
        <Tile to="/stats" icon={Coins} label="Invested" value={stats ? `$${Number(stats.total_cost_nzd).toFixed(0)}` : "—"} testid="home-tile-cost" />
      </section>

      <section className="mt-8">
        <div className="kicker mb-3">Today in the patch</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to="/garden/new" className="h-card p-4 flex items-center gap-3 hover:shadow-seal transition-shadow" data-testid="home-action-add-plant">
            <span className="w-11 h-11 rounded-full bg-sage-dark text-cream-soft flex items-center justify-center"><Plus size={20} /></span>
            <span className="flex-1">
              <div className="font-display text-xl text-sage-dark">Add a specimen</div>
              <div className="text-sm text-ink-soft">A seed, tuber, or cutting.</div>
            </span>
            <ChevronRight size={18} className="text-sage-dark" />
          </Link>
          <Link to="/beds" className="h-card p-4 flex items-center gap-3 hover:shadow-seal transition-shadow" data-testid="home-action-plan-bed">
            <span className="w-11 h-11 rounded-full bg-sage text-cream-soft flex items-center justify-center"><LayoutGrid size={20} /></span>
            <span className="flex-1">
              <div className="font-display text-xl text-sage-dark">Plan a bed</div>
              <div className="text-sm text-ink-soft">Lay out sizes, spacing, and place your plants.</div>
            </span>
            <ChevronRight size={18} className="text-sage-dark" />
          </Link>
        </div>
      </section>

      <section className="mt-10 mb-2" data-testid="home-community-section">
        <Link
          to="/community"
          className="h-card-double p-8 sm:p-10 flex flex-col items-center text-center hover:shadow-seal transition-shadow"
          data-testid="home-action-community"
        >
          <div className="kicker">The Community</div>
          <div className="font-display text-4xl sm:text-5xl text-sage-dark mt-3">The Flat Lay</div>
          <div className="font-display italic text-ink-soft text-lg mt-2 max-w-md">
            A communal cloth of every keeper's blooms. Pin yours, ask for an ID, or browse the register.
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span className="chip">Lay a bloom</span>
            <span className="chip">Ask "what is this"</span>
            <span className="chip">Browse confirmed</span>
          </div>
        </Link>
      </section>

      {user?.is_admin && (
        <section className="mt-8" data-testid="home-admin-section">
          <div className="kicker mb-3">Heritage Office</div>
          <Link
            to="/admin"
            className="h-card p-5 flex items-center gap-4 hover:shadow-seal transition-shadow"
            data-testid="home-action-admin"
          >
            <span className="w-14 h-14 rounded-full bg-ink text-cream-soft flex items-center justify-center shrink-0">
              <ShieldCheck size={22} />
            </span>
            <span className="flex-1 min-w-0">
              <div className="font-display text-2xl text-sage-dark">Admin portal</div>
              <div className="text-sm text-ink-soft">Review submissions, read letters, watch the patch.</div>
            </span>
            <ChevronRight size={18} className="text-sage-dark" />
          </Link>
        </section>
      )}
    </div>
  );
}

function Tile({ to, icon: Icon, label, value, accent, testid }) {
  return (
    <Link to={to} className="h-card p-4 hover:shadow-seal transition-shadow" data-testid={testid}>
      <Icon size={18} className={accent ? "text-terracotta" : "text-sage-dark"} />
      <div className="mt-2 font-display text-3xl text-sage-dark tabular-nums">{value}</div>
      <div className="kicker mt-1 text-[10px]">{label}</div>
    </Link>
  );
}
